import React, { useState, memo } from 'react';
import { TextField } from '@mui/material';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material';
import { Button, Form } from '../ui-kit';
import { IconXmark } from '../ui-kit/icons';
import { themeHash } from '../ui-kit/helpers';
import moment from 'moment';

const DateTimePicker = ({
  name,
  value,
  onChange,
  disabled,
  minDateTime,
  maxDateTime,
  customLabel,
  showClearButton,
  customThemeOverrides,
  allowOpenEnded,
  minutesInterval,
  dateOnly
}) => {
  const [previousValue, setPreviousValue] = useState(value);

  if (typeof minDateTime === 'string') minDateTime = moment(minDateTime);
  if (typeof maxDateTime === 'string') maxDateTime = moment(maxDateTime);

  // The TimePicker component only validates the time part of two datetimes.
  // To avoid false positives, if the min date is before the given date,
  // we will compare against 00:00
  // False positive - 15:00 (13-12-2030) < 17:00 (12-12-2030)
  // Correct - 15:00 (13-12-2030) > 00:00 (12-12-2030)
  let minTime = minDateTime && moment(minDateTime);
  if (minTime?.isBefore(value)) minTime = minTime.startOf('day');

  // Or if the given date is after the max date, we will compare against 23:59
  // False positive - 15:00 (12-12-2030) > 12:00 (13-12-2030)
  // Correct - 15:00 (12-12-2030) < 23:59 (13-12-2030)
  let maxTime = maxDateTime && moment(maxDateTime);
  if (maxTime?.isAfter(value)) maxTime = maxTime.endOf('day');

  let label = customLabel === undefined ? (name === 'startTime' ? I18n.t('from') : I18n.t('to')) : customLabel;

  // If we have no value, don't prefill with current time. We need declare
  // value as null, because undefined would cause a controlled/uncontrolled
  // input warning
  let valueMomentized = value && moment(value).isValid() ? moment(value) : null;
  if (valueMomentized && dateOnly) valueMomentized = valueMomentized.startOf('day');

  const setOpenEnded = value => {
    if (value) {
      // store the previous value so when open ended is turned off, we get the
      // original value back
      setPreviousValue(valueMomentized && valueMomentized.format());
      onChange(null);
    } else {
      onChange(previousValue);
    }
  };

  const customThemeHash = { ...themeHash };
  customThemeHash.components.MuiOutlinedInput = {
    ...customThemeOverrides,
    ...customThemeHash.components.MuiOutlinedInput
  };
  const customTheme = createTheme(customThemeHash);

  return (
    <ThemeProvider theme={customTheme}>
      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={I18n.locale}>
        <DatePicker
          className="mb-2"
          disabled={disabled}
          label={label}
          value={valueMomentized}
          minDate={minDateTime}
          maxDate={maxDateTime}
          inputFormat={'DD/MM/YYYY'}
          onChange={onChange}
          renderInput={params => <TextField {...params} />}
        />{' '}
        {dateOnly || (
          <TimePicker
            sx={{ width: '110px' }}
            ampm={false}
            disabled={disabled}
            value={valueMomentized}
            onChange={onChange}
            minTime={minTime}
            maxTime={maxTime}
            renderInput={params => <TextField {...params} />}
            timeSteps={{ minutes: minutesInterval || 1 }}
          />
        )}
      </LocalizationProvider>{' '}
      {showClearButton && (
        <Button variant="outline-secondary" onClick={() => onChange(null)}>
          <IconXmark />
        </Button>
      )}
      {allowOpenEnded && (
        <Form.Check
          id="setOpenEnded"
          onChange={e => setOpenEnded(e.target.checked)}
          checked={value === null}
          label={I18n.t('open_ended')}
        />
      )}
    </ThemeProvider>
  );
};

export default memo(DateTimePicker);
